import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Tooltip} from '@mantine/core';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import CreateFileModal from 'files/CreateFileModal';
import SearchInput from 'search/inputs/SearchInput';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import runModal from 'ui-library/runModal';

const REMOVE_SELECTION_TOOLTIP = 'Remove the selection before uploading new files';

const useStyles = createStyles(() => ({
  // Group sets `flex-grow: 0` on all of its direct children via a `& > *` rule of the same specificity as
  // this class; the doubled selector raises specificity above that so this wrapper still grows to fill
  // the available space.
  searchInputWrapper: {
    '&&': {
      flex: 1,
      minWidth: 0,
    },
  },
  tooltipTarget: {
    display: 'flex',
  },
}));

export interface SelectOrCreateFileInputProps {
  disabled?: boolean;
  maxItems?: number;
  onRemove: (fileId: string) => void;
  onSelect: (fileIds: Array<string>) => void;
  placeholder?: string;
  selected: Array<string>;
}

/**
 * A `SearchInput` restricted to files, with an adjacent "Upload" button that uploads new files (via
 * `CreateFileModal`) and selects them.
 *
 * For multi-select fields, the newly created files are appended to the selection. For single-select
 * fields, it replaces any existing selection - the "Upload" button is disabled while a file is already
 * selected, since there would be nothing to distinguish which file the field should hold.
 */
export default function SelectOrCreateFileInput({
  disabled = false,
  maxItems,
  onRemove,
  onSelect,
  placeholder = '',
  selected,
}: SelectOrCreateFileInputProps): JSX.Element {
  const {classes} = useStyles();
  const isBlockedByMaxItems = maxItems != null && selected.length >= maxItems;
  const isCreateDisabled = disabled || isBlockedByMaxItems;
  const disabledTooltip = !disabled && isBlockedByMaxItems ? REMOVE_SELECTION_TOOLTIP : null;

  async function handleCreateFiles(): Promise<void> {
    // If it is a multi-select and there is a max number of items,
    // then max number of items will be greater than number of selected items at this point
    const maxSelectableItems = maxItems != null ? maxItems - selected.length : undefined;
    const fileIds = await runModal(
      CreateFileModal,
      {maxItems: maxSelectableItems},
      {title: 'Create files', zIndex: ZINDEX_MODAL}
    );
    if (fileIds != null && fileIds.length > 0) {
      onSelect(fileIds);
    }
  }

  return (
    <Group noWrap={true} spacing="xs" align="stretch">
      <div className={classes.searchInputWrapper}>
        <SearchInput
          autoFocus={false}
          disableTyping={isCreateDisabled}
          disabled={disabled}
          isMulti={maxItems == null || maxItems > 1}
          maxItems={maxItems}
          onRemove={(fileId) => {
            if (fileId != null) {
              onRemove(fileId);
            }
          }}
          onSelect={(fileId) => {
            if (fileId != null) {
              onSelect([fileId]);
            }
          }}
          placeholder={placeholder}
          selected={selected}
          types={[ModelTypeConstants.FILE]}
        />
      </div>
      <Tooltip label={disabledTooltip ?? undefined} disabled={disabledTooltip == null}>
        <span className={classes.tooltipTarget}>
          <Button
            h="100%"
            disabled={isCreateDisabled}
            onClick={() => {
              handleCreateFiles();
            }}
            variant="tertiary"
            {...getTestProps({component: 'SelectOrCreateFileInput', element: 'createFileButton'})}
          >
            Upload
          </Button>
        </span>
      </Tooltip>
    </Group>
  );
}
